import logoDarkSrc from '@/assets/images/logo-long-dark.png';
import logoLightSrc from '@/assets/images/logo-long.png';
import { useAppearanceConfigModelStore } from '@/models/appearance-config/appearanceConfig';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { AppRegion } from '@/shared/constant/AppRegion';
import { AppTheme } from '@/shared/constant/AppTheme';
import { ErrorCode } from '@/shared/constant/error/ErrorCode';
import type { ErrorCodeMessageType } from '@/shared/constant/error/ErrorCodeMessageType';
import { Link, useLocation } from '@remix-run/react';
import { Button } from 'aui';
import { FoxIconRefreshCw01 } from 'aui-biz-icons';
import React from 'react';
import { CustomDomainError } from './components/custom-domain-error/CustomDomainError';
import { InvalidPathError } from './components/invalid-path-error/InvalidPathError';
import { NeedPreviewError } from './components/need-preview-error/NeedPreviewError';
import { NetworkError } from './components/network-error/NetworkError';
import { NotExistedError } from './components/not-existed-error/NotExistedError';
import { UnauthorizedError } from './components/unauthorized-error/UnauthorizedError';
import { UnknownError } from './components/unknown-error/UnknownError';
import { UnpublishError } from './components/unpublish-error/UnpublishError';

export type ErrorContentProps = {
  errorCodeMessage: ErrorCodeMessageType;
  isShowReloadButton?: boolean;
};

const sloganMap: Record<AppRegion, string> = {
  [AppRegion.Cn]: '节省研发团队的每一分钟',
  [AppRegion.Global]: 'Powered by',
};

export function ErrorContent(props: ErrorContentProps) {
  const { errorCodeMessage, isShowReloadButton } = props;

  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const appHomepage = APP_HOMEPAGE;
  const appTheme = useAppearanceConfigModelStore((x) => x.appTheme);
  const appName = APP_NAME;
  const appRegion = APP_REGION as AppRegion;

  const pathname = useLocation().pathname;
  const isPreview = React.useMemo(() => {
    return pathname.endsWith('/preview');
  }, [pathname]);

  return (
    <div className="flex h-full w-full flex-col overflow-auto">
      {!errorCodeMessage?.hideLogo && !IS_GLOBAL && (
        <Link to={appHomepage} className="flex flex-col justify-end pl-8 pt-8">
          <img
            className="block w-[140px] cursor-pointer object-cover"
            src={appTheme === AppTheme.Dark ? logoDarkSrc : logoLightSrc}
            alt="logo"
          />
        </Link>
      )}

      <div className="flex flex-1 flex-col items-center justify-center">
        {errorCodeMessage.errorCode === ErrorCode.CustomDomainError && (
          <CustomDomainError value={errorCodeMessage} />
        )}

        {errorCodeMessage.errorCode === ErrorCode.Unauthorized && (
          <UnauthorizedError value={errorCodeMessage} />
        )}

        {errorCodeMessage.errorCode === ErrorCode.Unpublish && (
          <UnpublishError value={errorCodeMessage} />
        )}

        {errorCodeMessage.errorCode === ErrorCode.NotExisted &&
          (isPreview ? (
            <NeedPreviewError value={errorCodeMessage} />
          ) : (
            <NotExistedError value={errorCodeMessage} />
          ))}

        {errorCodeMessage.errorCode === ErrorCode.Unknown && (
          <UnknownError value={errorCodeMessage} />
        )}

        {errorCodeMessage.errorCode === ErrorCode.NetworkError && (
          <NetworkError value={errorCodeMessage} />
        )}

        {errorCodeMessage.errorCode === ErrorCode.InvalidPath && (
          <InvalidPathError value={errorCodeMessage} />
        )}

        {isShowReloadButton && (
          <Button
            type="primary"
            theme="primary"
            className="mt-8"
            onClick={() => {
              window.location.reload();
            }}
            icon={<FoxIconRefreshCw01 />}
          >
            {clientI18n('onlineShare.resource.notExisted.reload')}
          </Button>
        )}
      </div>

      {!errorCodeMessage?.hideLogo && (
        <div className="flex flex-col items-center justify-center p-8 text-base font-base text-secondary">
          <div>
            <span className="mr-2">{sloganMap[appRegion]}</span>

            <Link
              className="cursor-pointer text-base font-base text-color hover:underline"
              to={appHomepage}
            >
              {appName}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
